import { useState, useEffect } from 'react';
import { X, UserPlus, Copy, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { consultationsService } from '@/services/api/consultations.service';
import { patientsService } from '@/services/api/patients.service';
import type { Appointment } from '@/types';
import { MedicationManager, type SelectedMedication } from './MedicationManager';

interface ConsultationModalProps {
  appointment: Appointment;
  patientName: string;
  patientHasUser?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ConsultationModal({
  appointment,
  patientName,
  patientHasUser = false,
  onClose,
  onSuccess,
}: ConsultationModalProps) {
  const [notes, setNotes] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [selectedMedications, setSelectedMedications] = useState<SelectedMedication[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Patient user creation
  const [hasUserAccount, setHasUserAccount] = useState(patientHasUser);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [userCredentials, setUserCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [copiedField, setCopiedField] = useState<'username' | 'password' | null>(null);

  // Log initial state
  console.log('[ConsultationModal] Initialized with:', {
    patientName,
    patientHasUser,
    appointmentId: appointment.id,
    patientId: appointment.patientId,
  });

  // Update hasUserAccount when prop changes (e.g., modal reopened after user creation)
  useEffect(() => {
    console.log('[ConsultationModal] patientHasUser prop changed to:', patientHasUser);
    setHasUserAccount(patientHasUser);
  }, [patientHasUser]);

  const handleCreateUserAccount = async () => {
    if (!appointment.patientId) return;

    console.log('[ConsultationModal] Creating user for patient:', appointment.patientId);
    setIsCreatingUser(true);
    try {
      const result = await patientsService.createUserAccount(appointment.patientId);
      
      console.log('[ConsultationModal] User created successfully:', {
        userId: result.userId,
        username: result.credentials.username
      });

      setUserCredentials({
        username: result.credentials.username,
        password: result.credentials.temporaryPassword,
      });
      setHasUserAccount(true);
      
      toast.success('Usuário criado com sucesso!');
      
      // Recarregar dados do paciente para atualizar o status
      try {
        const updatedPatient = await patientsService.getById(appointment.patientId);
        console.log('[ConsultationModal] Patient reloaded after user creation:', {
          patientId: updatedPatient.id,
          userId: updatedPatient.userId,
          hasUser: !!updatedPatient.userId
        });
        // Atualiza o estado local para refletir que agora tem usuário
        setHasUserAccount(!!updatedPatient.userId);
      } catch (reloadError) {
        console.error('[ConsultationModal] Error reloading patient data:', reloadError);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Erro ao criar usuário';
      toast.error(errorMessage);
      console.error('[ConsultationModal] Error creating user:', error);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleCopyToClipboard = async (text: string, field: 'username' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Copiado!');
    } catch (error) {
      toast.error('Erro ao copiar');
    }
  };

  const handleSubmit = async () => {
    if (!notes.trim()) {
      toast.error('Adicione anotações da consulta');
      return;
    }

    setIsSubmitting(true);
    try {
      const consultation = await consultationsService.create({
        appointmentId: appointment.id,
        patientId: appointment.patientId!,
        notes,
        diagnosis: diagnosis || undefined,
        medications: selectedMedications.map((sm) => ({
          medicationId: sm.medication.id,
          durationDays: sm.durationDays,
          instructions: sm.instructions || undefined,
        })),
      });

      // Mark consultation as completed
      await consultationsService.complete(consultation.id);

      toast.success('Consulta registrada e marcada como realizada!');
      onSuccess();
    } catch (error) {
      toast.error('Erro ao registrar consulta');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">Iniciar Consulta</h2>
              <p className="text-sm text-gray-500 mt-1">
                Paciente: <strong>{patientName}</strong>
              </p>
            </div>
            
            {/* User Account Status */}
            {appointment.patientId && (
              hasUserAccount ? (
                <div className="mr-3 px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  <span className="text-sm font-medium">Paciente já possui usuário</span>
                </div>
              ) : (
                <button
                  onClick={handleCreateUserAccount}
                  disabled={isCreatingUser}
                  className="mr-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  {isCreatingUser ? 'Criando...' : 'Criar Usuário'}
                </button>
              )
            )}
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* User Credentials Display */}
          {userCredentials && (
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Credenciais do Paciente
              </h3>
              <p className="text-sm text-purple-700 mb-3">
                Anote estas credenciais e forneça ao paciente:
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-purple-900 w-24">Usuário:</label>
                  <input
                    type="text"
                    value={userCredentials.username}
                    readOnly
                    className="flex-1 px-3 py-1 bg-white border border-purple-300 rounded text-sm"
                  />
                  <button
                    onClick={() => handleCopyToClipboard(userCredentials.username, 'username')}
                    className="p-2 text-purple-600 hover:text-purple-800"
                    title="Copiar"
                  >
                    {copiedField === 'username' ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-purple-900 w-24">Senha:</label>
                  <input
                    type="text"
                    value={userCredentials.password}
                    readOnly
                    className="flex-1 px-3 py-1 bg-white border border-purple-300 rounded text-sm font-mono"
                  />
                  <button
                    onClick={() => handleCopyToClipboard(userCredentials.password, 'password')}
                    className="p-2 text-purple-600 hover:text-purple-800"
                    title="Copiar"
                  >
                    {copiedField === 'password' ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <p className="text-xs text-purple-600 mt-2">
                * O paciente deverá alterar a senha no primeiro acesso
              </p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Anotações da Consulta *
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[120px]"
              placeholder="Descreva o histórico da consulta, sintomas, observações..."
            />
          </div>

          {/* Diagnosis */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Diagnóstico
            </label>
            <textarea
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={3}
              placeholder="Diagnóstico médico (opcional)"
            />
          </div>

          {/* Medications Section */}
          <MedicationManager
            selectedMedications={selectedMedications}
            onMedicationsChange={setSelectedMedications}
            className="border-t pt-6"
          />
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !notes.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Registrando...' : 'Registrar Consulta'}
          </button>
        </div>
      </div>
    </div>
  );
}
